import { useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

import { BENGUIDE_MODE_VIEW } from 'modules/auth/constants/globleConstants';

type UsePreviewThumbnailCaptureParams = {
  // Current renderer mode. The renderer has only two modes: edit and view.
  mode?: string;
  // The `?previewToken=` from the URL. A "preview" is VIEW mode WITH a token;
  // a public employee view is VIEW mode WITHOUT a token.
  previewToken?: string | null;
  // True once the guide content and its hero/section images have finished
  // loading, so the screenshot is not blank/partial. Computed by the caller
  // from the same image-loaded signals the edit-mode capture path uses.
  ready: boolean;
  // Best-effort capture. The thumbnail PUT is authenticated: a broker
  // previewing from the portal has a session so it succeeds, an anonymous
  // viewer does not (and it must not throw or disrupt the view).
  captureThumbnail: () => void;
};

/**
 * Fires an in-browser thumbnail capture ONCE when a broker previews a guide
 * (VIEW mode + a previewToken), so a translated (e.g. Spanish) guide gets a
 * correct preview thumbnail before it is published.
 *
 * It deliberately does NOT fire for plain public employee views (VIEW mode
 * without a previewToken), and a `useRef` guard ensures a single capture per
 * mount rather than on every render/scroll.
 */
export const usePreviewThumbnailCapture = ({
  mode,
  previewToken,
  ready,
  captureThumbnail,
}: UsePreviewThumbnailCaptureParams): void => {
  const hasCapturedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hasCapturedRef.current) {
      return;
    }
    const isPreview = mode === BENGUIDE_MODE_VIEW && !isEmpty(previewToken);
    if (!isPreview || !ready) {
      return;
    }
    hasCapturedRef.current = true;
    captureThumbnail();
  }, [mode, previewToken, ready, captureThumbnail]);
};
